import React from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, ChevronRight } from "lucide-react";
import "./AiEventList.css";

export interface AiEventItem {
  eventId: string | number;
  eventName: string;
  slugId?: string;
  eventStatus?: string;
  eventDate?: string;
  endDate?: string;
  eventVenue?: string;
  eventCity?: string;
  thumbImg?: string;
}

interface AiEventListProps {
  events: AiEventItem[];
  /** Short assistant caption above the list */
  caption?: string;
}

function formatDate(value?: string) {
  if (!value) return "Date TBD";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusClass(status?: string) {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "published" || s === "live") return "is-active";
  if (s === "draft") return "is-draft";
  if (s === "closed" || s === "completed") return "is-closed";
  if (s === "inactive" || s === "cancelled") return "is-inactive";
  return "is-muted";
}

function statusLabel(status?: string) {
  const s = (status || "unknown").toLowerCase();
  if (s === "active") return "Live";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Light **bold** support for short captions */
function renderCaption(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/**
 * Single Claude-style event list for Eventox AI chat.
 * Compact rows — easy to scan, tap opens event preview.
 */
const AiEventList: React.FC<AiEventListProps> = ({ events, caption }) => {
  const navigate = useNavigate();

  const openEvent = (event: AiEventItem) => {
    const id = event.eventId;
    if (id == null || id === "") return;
    // Mark return path so preview can show "Back to Ask Ev" and chat is restored
    navigate(`/events/${id}/preview`, {
      state: { fromAskEv: true, returnTo: "/ai" },
    });
  };

  if (!events?.length) {
    return (
      <div className="ai-elist">
        {caption ? <p className="ai-elist-caption">{renderCaption(caption)}</p> : (
          <div className="ai-elist-empty">No events to show.</div>
        )}
      </div>
    );
  }

  return (
    <div className="ai-elist">
      {caption ? <p className="ai-elist-caption">{renderCaption(caption)}</p> : null}

      <ul className="ai-elist-rows">
        {events.map((event, i) => {
          const img = event.thumbImg || "";
          return (
            <li
              key={`${event.eventId}-${i}`}
              className="ai-elist-row"
              style={{ animationDelay: `${Math.min(i, 14) * 28}ms` }}
            >
              <button type="button" onClick={() => openEvent(event)}>
                <div className="ai-elist-thumb" aria-hidden>
                  {img ? (
                    <img src={img} alt="" loading="lazy" />
                  ) : (
                    <span>{(event.eventName || "?").charAt(0)}</span>
                  )}
                </div>

                <div className="ai-elist-main">
                  <div className="ai-elist-top">
                    <h4 className="ai-elist-title">{event.eventName || "Untitled"}</h4>
                    <span className={`ai-elist-badge ${statusClass(event.eventStatus)}`}>
                      {statusLabel(event.eventStatus)}
                    </span>
                  </div>
                  <div className="ai-elist-meta">
                    <span>
                      <Calendar size={12} /> {formatDate(event.eventDate)}
                    </span>
                    {(event.eventVenue || event.eventCity) && (
                      <span>
                        <MapPin size={12} /> {event.eventVenue || event.eventCity}
                      </span>
                    )}
                  </div>
                </div>

                <ChevronRight size={16} className="ai-elist-chevron" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AiEventList;
